type Child = Node | string | null | undefined | false;

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs?: Record<string, unknown>,
  children?: Child[],
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (attrs) {
    for (const [key, value] of Object.entries(attrs)) {
      if (value === undefined || value === null) continue;
      if (key === 'class') node.className = String(value);
      else if (key === 'text') node.textContent = String(value);
      else if (key === 'html') node.innerHTML = String(value);
      else if (key.startsWith('on') && typeof value === 'function') {
        node.addEventListener(key.slice(2).toLowerCase(), value as EventListener);
      } else if (key === 'dataset') {
        for (const [dataKey, dataValue] of Object.entries(value as Record<string, string>)) {
          node.dataset[dataKey] = dataValue;
        }
      } else {
        node.setAttribute(key, String(value));
      }
    }
  }
  if (children) {
    for (const child of children) {
      if (child === null || child === undefined || child === false) continue;
      node.append(typeof child === 'string' ? document.createTextNode(child) : child);
    }
  }
  return node;
}

const collapsedSections = new Map<string, boolean>();

export function section(
  titleText: string,
  hintText?: string,
  key?: string,
  defaultOpen = false,
): { root: HTMLElement; body: HTMLElement } {
  const body = el('div', { class: 'section-body' });
  const chevron = el('span', { class: 'chevron', text: '▾' });
  const head = el('h2', {}, [
    el('span', { class: 'section-lead' }, [
      chevron,
      el('span', { class: 'section-title', text: titleText }),
    ]),
    hintText ? el('span', { class: 'hint', text: hintText }) : null,
  ]);
  const root = el('div', { class: 'section' }, [head, body]);

  const collapsed = key ? collapsedSections.get(key) ?? !defaultOpen : !defaultOpen;
  const setCollapsed = (value: boolean): void => {
    root.classList.toggle('collapsed', value);
    head.setAttribute('aria-expanded', String(!value));
    if (key) collapsedSections.set(key, value);
  };
  setCollapsed(collapsed);
  head.addEventListener('click', () => setCollapsed(!root.classList.contains('collapsed')));

  return { root, body };
}

export function numberInput(
  value: number,
  opts: { min?: number; max?: number; step?: number; width?: string; name?: string } = {},
): HTMLInputElement {
  return el('input', {
    type: 'number',
    value: String(value),
    name: opts.name,
    min: opts.min,
    max: opts.max,
    step: opts.step ?? 'any',
    style: opts.width ? `width:${opts.width}` : undefined,
  });
}

export function row(children: Child[]): HTMLDivElement {
  return el('div', { class: 'row' }, children);
}

export function labeled(labelText: string, control: Node, big = false): HTMLDivElement {
  return el('div', { class: 'row' }, [el('label', { class: big ? 'big' : '', text: labelText }), control]);
}

export function button(
  text: string,
  opts: { class?: string; onClick?: () => void; title?: string; disabled?: boolean } = {},
): HTMLButtonElement {
  const node = el('button', { class: opts.class, text, title: opts.title, onclick: opts.onClick });
  if (opts.disabled) node.disabled = true;
  return node;
}

export function fmtInt(value: number): string {
  if (!Number.isFinite(value)) return '—';
  return Math.round(value).toLocaleString();
}

export function fmtNum(value: number, digits = 2): string {
  if (!Number.isFinite(value)) return '—';
  return value.toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

export function fmtVolume(mm3: number): string {
  const value = Math.abs(mm3);
  if (value >= 1_000_000) return `${fmtNum(value / 1_000_000, 3)} l`;
  if (value >= 1000) return `${fmtNum(value / 1000, 2)} cm³`;
  return `${fmtNum(value, 0)} mm³`;
}

export function fmtArea(mm2: number): string {
  if (mm2 >= 1_000_000) return `${fmtNum(mm2 / 1_000_000, 2)} m²`;
  if (mm2 >= 1000) return `${fmtNum(mm2 / 1000, 2)} cm²`;
  return `${fmtNum(mm2, 1)} mm²`;
}

export function fmtBytes(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${fmtNum(bytes / (1024 * 1024), 2)} MB`;
  if (bytes >= 1024) return `${fmtNum(bytes / 1024, 1)} kB`;
  return `${bytes} B`;
}
